import type { ToastServiceMethods } from 'primevue/toastservice';
import { getI18n } from './i18n';
import type { Model } from '@kp/js-model';

/**
 * Creates a function that can only be called "debounced", means, only all n milliseconds.
 *
 * @param      {Function}  fn      The function to execute
 * @param      {Number}    millis  The nr of milliseconds in which the function is NOT called again
 * @return     {Function}  a new Function that can rapidly be called, but is only invoked max all n milliseconds.
 */
export function debounce(fn: (...args: any) => void, millis = 350) {
    // hoisted timeout variable
    let timeout: number | null = null;
    const callbackFn = () => (timeout = null);

    return function (...args: any) {
        if (timeout) {
            return;
        }
        timeout = setTimeout(callbackFn, millis);
        fn(...args);
    };
}

export function intToFilesizeStr(bytes: number) {
    if (bytes / 1000000000 > 1) {
        return (bytes / 1000000000).toFixed(2) + ' GB';
    }
    if (bytes / 1000000 > 1) {
        return (bytes / 1000000).toFixed(2) + ' MB';
    }
    if (bytes / 1000 > 1) {
        return (bytes / 1000).toFixed(2) + ' KB';
    }
    return bytes + ' B';
}

export function todoAlert() {
    alert('TODO');
}

/**
 * Tries to make sense of the given "Error", which may be a string,
 * an error object, some kind of response.... you name it.
 * @param {any} error The error "thingie"
 * @return A string representing the error msg.
 */
export function errToStr(error: any): string {
    const vueI18n = getI18n();
    let msg = vueI18n.t('unknown_error');

    if (error) {
        // Try to extract a data property:
        const data = error?.response?.data || error?.response?.response?.data;

        if (data?.error?.msg) {
            msg = `${data.error.code}: ${data.error.msg}`;
        } else if (error?.response?.responseText) {
            const obj = JSON.parse(error.response.responseText);
            if (obj?.error?.msg) {
                msg = `${obj.error.code}: ${obj.error.msg}`;
            } else {
                msg = String(error.response.responseText);
            }
        } else if (error.responseText) {
            const obj = JSON.parse(error.responseText);
            if (obj?.error?.msg) {
                msg = `${obj.error.code}: ${obj.error.msg}`;
            } else {
                msg = String(error.responseText);
            }
        } else if (error?.error) {
            msg = `${error.error.code}: ${error.error.msg}`;
        } else if (typeof error === 'string') {
            try {
                const obj = JSON.parse(error);
                if (obj?.error?.msg) {
                    msg = `${obj.error.code}: ${obj.error.msg}`;
                } else {
                    msg = String(error);
                }
            } catch (x) {
                msg = String(error);
            }
        } else {
            msg = String(error);
        }
    }
    return msg;
}

export function errToToast(error: any, toast: ToastServiceMethods) {
    toast.add({
        severity: 'error',
        summary: getI18n().t('fehler'),
        detail: errToStr(error),
    });
}

export function reallyCancel(rec: Model | null): boolean {
    if (rec?.isDirty()) {
        if (!window.confirm(getI18n().t('really_cancel'))) {
            return false;
        }
    }
    return true;
}

export function mimeToIconCls(mime: string | null) {
    switch (mime) {
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return 'fa-regular fa-file-spreadsheet';
        case 'application/zip':
            return 'fa-regular fa-file-zip';
        default:
            return 'fa-regular fa-file';
    }
}

export function fileExtToMime(ext: string) {
    switch (ext.toLocaleLowerCase()) {
        case 'pdf':
            return 'application/pdf';
        case 'doc':
        case 'docx':
            return 'application/msword';
        case 'ppt':
        case 'pptx':
            return 'application/vnd.ms-powerpoint';
        case 'xls':
            return 'application/vnd.ms-excel';
        case 'xlsx':
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        default:
            return 'application/octet-stream';
    }
}

export function capitalize(s: string | null): string {
    s = s || '';
    return s[0].toUpperCase() + s.slice(1);
}

export function filenameToIconCls(filename: string | null): string {
    if (!filename) {
        return 'fa-regular fa-file';
    }
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
        case 'xls':
        case 'xlsx':
            return 'fa-regular fa-file-spreadsheet';
        case 'zip':
            return 'fa-regular fa-file-zip';
        default:
            return 'fa-regular fa-file';
    }
}
